import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  WritableSignal,
  effect,
  inject,
  input,
  signal,
  computed,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViolationRulesHttpService } from 'src/app/services/httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import {
  ViolationRule,
  RuleStatus,
  ViolationRuleUpdateApiModel,
  Severity,
  RuleTag,
} from 'src/app/models/api/governance/violationRule.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViolationRulesViewModel } from 'src/app/models/viewModels/violationRulesViewModel';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { Platform } from 'src/app/models/api/common';
import { AggregationsHttpServiceService } from 'src/app/services/httpServices/aggregationsHttpService/aggregations-http-service.service';
import {
  AggregationsType,
  AggregationType,
  AggregationDict,
  AIDRAggregations,
  AggregationsModel,
} from 'src/app/models/api/aggregations.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { catchError, forkJoin, of } from 'rxjs';
import { isAiRule } from 'src/app/common/aiServiceUtils';
import { ITimelineItem } from 'src/app/components/timeline/timeline.component';
import { parseFixSuggestionFieldToTimelineWithNestedBullets } from 'src/app/common/fixSuggestionUtils';

@Component({
  selector: 'app-violation-rule-details',
  templateUrl: './violation-rule-details.component.html',
  styleUrls: ['./violation-rule-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViolationRuleDetailsComponent {
  public violationRule = input.required<string>();
  public integration = input.required<string>();
  public viewModel = input<ViolationRulesViewModel>();
  public policy = input<string | null>(null);
  public ruleType = input<'detection' | 'violation' | null>(null);

  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _rulesHttp = inject(ViolationRulesHttpService);
  private readonly _aggregationsHttp = inject(AggregationsHttpServiceService);
  private readonly _permissionsService = inject(PermissionsService);

  private _model: WritableSignal<ViolationRule | null> = signal<ViolationRule | null>(null);
  public model = this._model.asReadonly();
  private _policiesAggregations: WritableSignal<AggregationDict> = signal({});
  private _aidrAggregations: WritableSignal<AIDRAggregations> = signal({});

  public isAidrRule = computed(() => {
    const rule = this.model();
    return rule ? isAiRule(rule) : false;
  });

  public canEditSPM = computed(() => {
    const integrationId = this.integration() ?? this.viewModel()?.integrationId ?? null;
    return this._permissionsService.userHasPermission(PERMISSIONS.VIOLATION_OPERATION, integrationId ?? undefined);
  });

  public isEditDisabled = computed(() => {
    return this.isAidrRule() || !this.canEditSPM();
  });

  public count = computed(() => {
    // First try to get count from viewModel if available
    const viewModelCount = this.viewModel()?.count;
    if (viewModelCount !== undefined && viewModelCount !== null) {
      return viewModelCount;
    }

    // Otherwise compute from aggregations
    const rule = this.model();
    if (!rule) return null;

    const isRuleAi = isAiRule(rule);
    let violationsAmount = this._policiesAggregations()[rule.id] || 0;

    if (isRuleAi && this._aidrAggregations()[rule.id]) {
      violationsAmount = this._aidrAggregations()[rule.id];
    }

    return violationsAmount;
  });

  public triageSuggestion = computed<ITimelineItem[]>(() => {
    const rule = this.model();
    if (!rule?.triageSuggestion) return [];

    return rule.triageSuggestion
      .split(/\r?\n/)
      .filter((line) => line.trim() !== '')
      .map((line) => ({ text: line.trim() }));
  });

  public fixSuggestion = computed<ITimelineItem[]>(() => {
    const rule = this.model();
    if (!rule?.fixSuggestion) return [];

    return parseFixSuggestionFieldToTimelineWithNestedBullets(rule.fixSuggestion);
  });

  public isStatusDropdownOpen = signal(false);
  public isSeverityDropdownOpen = signal(false);
  public readonly RuleStatus = RuleStatus;
  public readonly Severity = Severity;
  public readonly severityOptions = [
    { value: Severity.high, label: 'High', displayLabel: 'High' },
    { value: Severity.medium, label: 'Medium', displayLabel: 'Medium' },
    { value: Severity.low, label: 'Low', displayLabel: 'Low' },
  ];

  public getPlatformIcon(): string {
    const rule = this.model();
    if (!rule) return '';
    try {
      return getPlatformLogoPath(rule.platform as Platform);
    } catch {
      return '';
    }
  }

  public getStatusDisplay(status: RuleStatus): string {
    switch (status) {
      case RuleStatus.on:
        return 'Active';
      case RuleStatus.off:
        return 'Inactive';
      default:
        return status;
    }
  }

  public getTriageText(text: string): string {
    if (!text) return text;
    // Remove {resource_name} placeholder from the text
    return text.replace(/\{resource_name\}/g, '').trim();
  }

  public getFixSuggestionCopyText(): string {
    const rule = this.model();
    if (!rule?.fixSuggestion) return '';

    const fixSuggestionItems = this.fixSuggestion();

    if (fixSuggestionItems.length === 0) {
      return rule.fixSuggestion;
    }

    return fixSuggestionItems
      .map((item, index) => {
        const mainItem = `${index + 1}. ${item.text}`;
        const nestedItems =
          item.nestedItems && item.nestedItems.length > 0
            ? item.nestedItems.map((nestedItem) => `   • ${nestedItem.text}`).join('\n')
            : '';

        return nestedItems ? `${mainItem}\n${nestedItems}` : mainItem;
      })
      .join('\n');
  }

  public toggleStatusDropdown(): void {
    if (this.isEditDisabled()) return;
    this.isStatusDropdownOpen.set(!this.isStatusDropdownOpen());
    if (this.isStatusDropdownOpen()) {
      this.isSeverityDropdownOpen.set(false);
    }
  }

  public toggleSeverityDropdown(): void {
    if (this.isEditDisabled()) return;
    this.isSeverityDropdownOpen.set(!this.isSeverityDropdownOpen());
    if (this.isSeverityDropdownOpen()) {
      this.isStatusDropdownOpen.set(false);
    }
  }

  public updateSeverity(newSeverity: Severity): void {
    const rule = this.model();
    if (!rule || this.isEditDisabled()) return;

    const integrationId =
      this.integration() ??
      this._route.snapshot.queryParamMap.get('integration') ??
      this.viewModel()?.integrationId ??
      null;

    const policyId =
      this.policy() ?? this._route.snapshot.queryParamMap.get('policy') ?? this.viewModel()?.policyId ?? null;

    if (!integrationId || !policyId) {
      return;
    }

    // Optimistically update the UI immediately
    const updatedRule = { ...rule, severity: newSeverity };
    this._model.set(updatedRule);
    this.isSeverityDropdownOpen.set(false);

    // Update the viewModel if it exists (for table sync)
    const vm = this.viewModel();
    if (vm) {
      vm.severity = newSeverity;
      // Update severityValue for sorting
      const severityValues: Record<Severity, number> = {
        [Severity.high]: 3,
        [Severity.medium]: 2,
        [Severity.low]: 1,
        [Severity.informational]: 0,
      };
      vm.severityValue = severityValues[newSeverity];
    }

    // Then update the backend
    const update: ViolationRuleUpdateApiModel = {
      severity: newSeverity,
    };

    this._rulesHttp
      .updateViolationRule(integrationId, policyId, rule.id, update)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Trigger a custom event to notify parent components to refresh
          window.dispatchEvent(
            new CustomEvent('violation-rule-updated', {
              detail: { ruleId: rule.id, integrationId, policyId, severity: newSeverity },
            })
          );
        },
        error: () => {
          // Revert on error
          this._model.set(rule);
          // Revert viewModel if update failed
          if (vm) {
            vm.severity = rule.severity;
            const severityValues: Record<Severity, number> = {
              [Severity.high]: 3,
              [Severity.medium]: 2,
              [Severity.low]: 1,
              [Severity.informational]: 0,
            };
            vm.severityValue = severityValues[rule.severity];
          }
        },
      });
  }

  public updateStatus(newStatus: RuleStatus): void {
    const rule = this.model();
    if (!rule || this.isEditDisabled()) return;

    const integrationId =
      this.integration() ??
      this._route.snapshot.queryParamMap.get('integration') ??
      this.viewModel()?.integrationId ??
      null;

    const policyId =
      this.policy() ?? this._route.snapshot.queryParamMap.get('policy') ?? this.viewModel()?.policyId ?? null;

    if (!integrationId || !policyId) {
      return;
    }

    const updatedRule = { ...rule, status: newStatus };
    this._model.set(updatedRule);
    this.isStatusDropdownOpen.set(false);

    const vm = this.viewModel();
    if (vm) {
      vm.status = newStatus;
    }

    // Then update the backend
    const update: ViolationRuleUpdateApiModel = {
      status: newStatus,
    };

    this._rulesHttp
      .updateViolationRule(integrationId, policyId, rule.id, update)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Trigger a custom event to notify parent components to refresh
          window.dispatchEvent(
            new CustomEvent('violation-rule-updated', {
              detail: { ruleId: rule.id, integrationId, policyId, status: newStatus },
            })
          );
        },
        error: () => {
          // Revert on error
          this._model.set(rule);
          // Revert viewModel if update failed
          if (vm) {
            vm.status = rule.status;
          }
        },
      });
  }

  public navigateToViolations(): void {
    const rule = this.model();
    if (!rule) return;

    const ruleId =
      this.violationRule() ??
      this._route.snapshot.queryParamMap.get('violationRule') ??
      this.viewModel()?.ruleId ??
      null;

    const integrationId =
      this.integration() ??
      this._route.snapshot.queryParamMap.get('integration') ??
      this.viewModel()?.integrationId ??
      this.viewModel()?.violationsEndpointQuery?.['integration'] ??
      null;

    const policyId =
      this.policy() ??
      this._route.snapshot.queryParamMap.get('policy') ??
      this.viewModel()?.policyId ??
      this.viewModel()?.violationsEndpointQuery?.['policy'] ??
      null;

    if (!integrationId || !policyId || !ruleId) {
      return;
    }

    // Check if this is an AI rule
    const isRuleAi = isAiRule(rule);

    if (isRuleAi) {
      // Navigate to AI findings page for AI rules (individuals tab)
      this._router.navigate(['/app/ai-findings'], {
        queryParams: {
          q: `search:"${ruleId}" integration:${integrationId}`,
          view: 'individuals',
        },
      });
    } else {
      // Construct the filter query string in the format: key:value separated by spaces
      const queryString = `integration:${integrationId} violationRule:${ruleId} violationStatus:open policy:${policyId}`;

      this._router.navigate(['/app/violations'], {
        queryParams: {
          q: queryString,
        },
      });
    }
  }

  // Load rule on param changes
  public _load = effect(
    () => {
      const ruleId =
        this.violationRule() ??
        this._route.snapshot.queryParamMap.get('violationRule') ??
        this.viewModel()?.ruleId ??
        null;

      const integrationId =
        this.integration() ??
        this._route.snapshot.queryParamMap.get('integration') ??
        this.viewModel()?.integrationId ??
        this.viewModel()?.violationsEndpointQuery?.['integration'] ??
        null;

      const policyId =
        this.policy() ??
        this._route.snapshot.queryParamMap.get('policy') ??
        this.viewModel()?.policyId ??
        this.viewModel()?.violationsEndpointQuery?.['policy'] ??
        null;

      // Read ruleType from input (passed from side-panel config) or fallback to query param
      const ruleType =
        this.ruleType() ??
        this._route.snapshot.queryParamMap.get('ruleType') ??
        (this.viewModel()?.tags?.includes(RuleTag.AIDR) ? 'detection' : 'violation');

      if (!ruleId || !integrationId || !policyId) {
        return;
      }

      // Determine if this is a detection rule from the ruleType query param
      const isDetectionRule = ruleType === 'detection';

      this._rulesHttp
        .getRule(integrationId, policyId, ruleId, isDetectionRule)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((rule) => this._model.set(rule));

      // Load aggregations to get the count
      this._loadAggregations(integrationId, policyId);
    },
    { allowSignalWrites: true }
  );

  private _loadAggregations(integrationId: string, policyId: string): void {
    const policiesAggregations$ = this._aggregationsHttp
      .getAggregations(AggregationsType.common, {
        policy: [policyId],
      } as ISelectedFilters)
      .pipe(
        catchError(() => {
          return of({ aggregationsData: {} } as AggregationsModel);
        })
      );

    const aidrAggregations$ = this._aggregationsHttp.getAIDRAggregations(integrationId).pipe(
      catchError(() => {
        return of({} as AIDRAggregations);
      })
    );

    forkJoin([policiesAggregations$, aidrAggregations$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([policiesAggregations, aidrAggregations]) => {
        this._policiesAggregations.set(
          (policiesAggregations.aggregationsData[AggregationType.violationsRuleAmountPerPolicy]?.data ??
            {}) as AggregationDict
        );
        this._aidrAggregations.set(aidrAggregations);
      });
  }
}
